/**
 * MINI GERENCIAMENTO — MENSALIDADES (controle INTERNO da equipe).
 *
 * Não é cobrança do e-Torneios: sem gateway, sem Pix, sem cobrança automática.
 * O professor/equipe registra o combinado e o que foi pago; a SITUAÇÃO
 * (pago / pendente / atrasado) é sempre DERIVADA do pagamento + vencimento.
 *
 * INVARIANTE: nada aqui bloqueia o lado esportivo — aluno atrasado continua
 * podendo se inscrever, lutar e receber medalha.
 */
import { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import {
  CalendarPlus, CheckCircle2, ChevronLeft, ChevronRight, Loader2, Trash2, Undo2,
} from 'lucide-react';
import { minhaEquipeApi } from '../../lib/api';
import {
  competenciaDe, competenciaTexto, CORES_SITUACAO, formatarValor, rotuloSituacao,
  somarMeses, situacaoMensalidade, turmaPadraoDoDia,
} from '../../lib/minhaEquipe';

export default function MensalidadesEquipe({ turmas = [] }) {
  const [competence, setCompetence] = useState(competenciaDe());
  const [situacao, setSituacao] = useState('todas');
  const [turmaId, setTurmaId] = useState('');
  const [dados, setDados] = useState({ mensalidades: [], resumo: {} });
  const [carregando, setCarregando] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [novoAberto, setNovoAberto] = useState(false);
  const [novo, setNovo] = useState({ athlete_id: '', amount: '', due_date: '', class_group_id: '' });
  const [alunos, setAlunos] = useState([]);

  // PRÉ-SELEÇÃO: uma turma só (ou a única com aula hoje) já vem escolhida —
  // a lista abre na turma do professor e o "Gerar o mês" já aponta para ela.
  // Só preenche se o filtro estiver vazio (a escolha do usuário manda).
  useEffect(() => {
    const padrao = turmaPadraoDoDia(turmas);
    if (!turmaId && padrao) setTurmaId(padrao);
  }, [turmas, turmaId]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const params = { competence };
      if (situacao !== 'todas') params.situacao = situacao;
      if (turmaId) params.class_group_id = turmaId;
      const { data } = await minhaEquipeApi.mensalidades(params);
      setDados(data || { mensalidades: [], resumo: {} });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar as mensalidades');
    } finally {
      setCarregando(false);
    }
  }, [competence, situacao, turmaId]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    minhaEquipeApi.alunos().then((r) => setAlunos(r.data || [])).catch(() => setAlunos([]));
  }, []);

  const turmaSelecionada = turmas.find((t) => t.id === turmaId);
  // "Gerar o mês" é ação DA TURMA: exige uma turma escolhida e a permissão de editá-la
  const podeGerarMes = Boolean(turmaId) && turmaSelecionada?.pode_editar !== false;
  // O lançamento manual ("Lançar uma") também é ação DA TURMA quando aponta para
  // uma: sem poder editá-la o servidor recusa (403) — aqui só evitamos o clique
  // perdido. "Sem turma" é lançamento próprio: sempre permitido.
  const alvoDoLancamento = novo.class_group_id || turmaId || '';
  const podeLancarNaTurma = !alvoDoLancamento
    || turmas.find((t) => t.id === alvoDoLancamento)?.pode_editar !== false;

  const gerarMes = async () => {
    if (!turmaId) { toast.error('Escolha a turma para gerar o mês'); return; }
    if (!podeGerarMes) {
      toast.error('Você não pode gerar o mês desta turma (só quem criou, o responsável pela equipe ou o professor da turma)');
      return;
    }
    setGerando(true);
    try {
      const { data } = await minhaEquipeApi.gerarMes({ class_group_id: turmaId, competence });
      toast.success(
        data.criadas
          ? `${data.criadas} mensalidade(s) gerada(s) para ${competenciaTexto(competence)}${
              data.ja_existiam ? ` — ${data.ja_existiam} já existia(m)` : ''}`
          : `Nada a gerar: ${data.ja_existiam} já existia(m) em ${competenciaTexto(competence)}`,
      );
      await carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao gerar o mês');
    } finally {
      setGerando(false);
    }
  };

  const marcarPago = async (registro) => {
    try {
      await minhaEquipeApi.pagarMensalidade(registro.id);
      toast.success(`${registro.athlete_name}: pagamento registrado`);
      await carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao registrar o pagamento');
    }
  };

  const desfazer = async (registro) => {
    try {
      await minhaEquipeApi.desfazerPagamento(registro.id);
      toast.success('Pagamento desfeito');
      await carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao desfazer');
    }
  };

  const apagar = async (registro) => {
    if (!window.confirm(`Apagar o lançamento de ${registro.athlete_name} (${competenciaTexto(registro.competence)})?`)) return;
    try {
      await minhaEquipeApi.apagarMensalidade(registro.id);
      toast.success('Lançamento apagado');
      await carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao apagar');
    }
  };

  const criar = async () => {
    if (!novo.athlete_id) { toast.error('Escolha o aluno'); return; }
    if (!podeLancarNaTurma) {
      toast.error('Você não pode lançar mensalidade nesta turma (só quem criou, o responsável pela equipe ou o professor da turma)');
      return;
    }
    try {
      await minhaEquipeApi.criarMensalidade({
        athlete_id: novo.athlete_id,
        competence,
        // o lançamento manual TAMBÉM guarda a turma (senão ele sumiria do filtro);
        // sem turma escolhida no formulário, usa a turma do filtro
        class_group_id: novo.class_group_id || turmaId || null,
        amount: novo.amount === '' ? null : Number(novo.amount),
        due_date: novo.due_date || null,
      });
      toast.success('Mensalidade lançada');
      setNovoAberto(false);
      setNovo({ athlete_id: '', amount: '', due_date: '', class_group_id: '' });
      await carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao lançar');
    }
  };

  const resumo = dados.resumo || {};

  return (
    <div className="space-y-4" data-testid="mensalidades-equipe">
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5">
        <p className="text-micro text-amber-300">
          Controle <strong>interno da equipe</strong> — não é cobrança do e-Torneios (sem gateway,
          sem Pix, sem cobrança automática). E não bloqueia nada no lado esportivo: aluno com
          mensalidade atrasada continua podendo competir.
        </p>
      </div>

      {/* Filtros + ações ("Lançar uma" / "Gerar o mês da turma") ficam fixos no
          topo: a tabela do mês é longa e as ações ficavam fora da tela ao rolar. */}
      <div className="sticky top-0 z-10 -mt-4 flex flex-wrap items-end gap-3 bg-[#0A0A0A] pb-2 pt-4">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => setCompetence(somarMeses(competence, -1))}
                  data-testid="mes-anterior" className="border-[#27272A] text-[#A1A1AA] hover:text-white">
            <ChevronLeft size={16} />
          </Button>
          <span className="text-sm text-white font-semibold w-36 text-center" data-testid="competencia">
            {competenciaTexto(competence)}
          </span>
          <Button variant="outline" size="sm" onClick={() => setCompetence(somarMeses(competence, 1))}
                  data-testid="mes-seguinte" className="border-[#27272A] text-[#A1A1AA] hover:text-white">
            <ChevronRight size={16} />
          </Button>
        </div>

        <div className="w-44">
          <Select value={situacao} onValueChange={setSituacao}>
            <SelectTrigger data-testid="filtro-situacao" className="bg-[#141414] border-[#27272A] text-white">
              <SelectValue placeholder="Situação" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
              <SelectItem value="todas" className="text-white">Todas as situações</SelectItem>
              <SelectItem value="paga" className="text-white">Pagas</SelectItem>
              <SelectItem value="pendente" className="text-white">Pendentes</SelectItem>
              <SelectItem value="atrasada" className="text-white">Atrasadas</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="w-52">
          <Select value={turmaId || 'todas'} onValueChange={(v) => setTurmaId(v === 'todas' ? '' : v)}>
            <SelectTrigger data-testid="filtro-turma" className="bg-[#141414] border-[#27272A] text-white">
              <SelectValue placeholder="Turma" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
              <SelectItem value="todas" className="text-white">Todas as turmas</SelectItem>
              {turmas.map((t) => (
                <SelectItem key={t.id} value={t.id} className="text-white">{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-2 ml-auto">
          <Button variant="outline" onClick={() => setNovoAberto((v) => !v)}
                  data-testid="lancar-mensalidade"
                  className="border-[#27272A] text-[#A1A1AA] hover:text-white">
            Lançar uma
          </Button>
          <Button onClick={gerarMes} disabled={gerando || !podeGerarMes} data-testid="gerar-mes"
                  className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold">
            {gerando ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarPlus size={16} className="mr-1" />}
            Gerar o mês da turma
          </Button>
        </div>
      </div>

      {novoAberto && (
        <div className="rounded-lg border border-[#27272A] bg-[#0F0F0F] p-3 grid grid-cols-1 sm:grid-cols-5 gap-3 items-end"
             data-testid="form-mensalidade">
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-[#A1A1AA] text-xs">Aluno</Label>
            <Select value={novo.athlete_id || 'none'} onValueChange={(v) => setNovo({ ...novo, athlete_id: v === 'none' ? '' : v })}>
              <SelectTrigger data-testid="novo-aluno" className="bg-[#1A1A1A] border-[#27272A] text-white">
                <SelectValue placeholder="Selecione o aluno" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                <SelectItem value="none" className="text-white">Selecione...</SelectItem>
                {alunos.map((a) => (
                  <SelectItem key={a.id} value={a.id} className="text-white">{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#A1A1AA] text-xs">Turma (opcional)</Label>
            <Select
              value={novo.class_group_id || turmaId || 'none'}
              onValueChange={(v) => setNovo({ ...novo, class_group_id: v === 'none' ? '' : v })}
            >
              <SelectTrigger data-testid="novo-turma" className="bg-[#1A1A1A] border-[#27272A] text-white">
                <SelectValue placeholder="Sem turma" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                <SelectItem value="none" className="text-white">Sem turma</SelectItem>
                {turmas.map((t) => (
                  <SelectItem key={t.id} value={t.id} className="text-white">{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#A1A1AA] text-xs">Valor (R$)</Label>
            <Input type="number" step="0.01" inputMode="decimal" value={novo.amount}
                   onChange={(e) => setNovo({ ...novo, amount: e.target.value })}
                   placeholder="150,00" data-testid="novo-valor"
                   className="bg-[#1A1A1A] border-[#27272A] text-white" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#A1A1AA] text-xs">Vencimento</Label>
            <Input type="date" value={novo.due_date}
                   onChange={(e) => setNovo({ ...novo, due_date: e.target.value })}
                   data-testid="novo-vencimento"
                   className="bg-[#1A1A1A] border-[#27272A] text-white" />
          </div>
          <div className="sm:col-span-5 flex justify-end">
            <Button onClick={criar} data-testid="salvar-mensalidade"
                    disabled={!podeLancarNaTurma}
                    title={podeLancarNaTurma ? undefined : 'Você só pode ver esta turma'}
                    className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold disabled:opacity-50">
              Lançar em {competenciaTexto(competence, true)}
            </Button>
          </div>
        </div>
      )}

      {/* Resumo do mês */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="resumo-mensalidades">
        {[
          { rotulo: 'Lançamentos', valor: resumo.total ?? 0, cor: 'text-white' },
          { rotulo: 'Pagas', valor: resumo.pagas ?? 0, cor: 'text-emerald-400' },
          { rotulo: 'Pendentes', valor: resumo.pendentes ?? 0, cor: 'text-amber-400' },
          { rotulo: 'Atrasadas', valor: resumo.atrasadas ?? 0, cor: 'text-red-400' },
          { rotulo: 'Em aberto', valor: formatarValor(resumo.valor_em_aberto ?? 0), cor: 'text-white' },
        ].map((c) => (
          <div key={c.rotulo} className="rounded-lg border border-[#27272A] bg-[#141414] p-3">
            <p className="text-micro uppercase tracking-wider text-[#71717A]">{c.rotulo}</p>
            <p className={`text-lg font-bold ${c.cor}`}>{c.valor}</p>
          </div>
        ))}
      </div>

      {carregando ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span></div>
      ) : (dados.mensalidades || []).length === 0 ? (
        <p className="text-sm text-[#71717A]" data-testid="mensalidades-vazio">
          Nenhum lançamento em {competenciaTexto(competence)}. Use "Gerar o mês da turma".
        </p>
      ) : (
        <div className="rounded-lg border border-[#27272A] overflow-hidden">
          <table className="tabela w-full text-sm">
            <thead className="bg-[#1A1A1A]">
              <tr className="text-micro uppercase tracking-wider text-[#71717A]">
                <th className="text-left px-3 py-2">Aluno</th>
                <th className="text-left px-3 py-2">Competência</th>
                <th className="text-right px-3 py-2">Valor</th>
                <th className="text-left px-3 py-2">Vencimento</th>
                <th className="text-left px-3 py-2">Situação</th>
                <th className="text-right px-3 py-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {(dados.mensalidades || []).map((m) => {
                const sit = situacaoMensalidade(m);
                return (
                  <tr key={m.id} data-testid={`mensalidade-${m.id}`} className="border-t border-[#27272A]">
                    <td className="px-3 py-2 text-white">{m.athlete_name}</td>
                    <td className="px-3 py-2 text-[#A1A1AA]">{competenciaTexto(m.competence)}</td>
                    <td className="px-3 py-2 text-right text-white">{formatarValor(m.amount)}</td>
                    <td className="px-3 py-2 text-[#A1A1AA]">
                      {m.due_date ? m.due_date.split('-').reverse().join('/') : '—'}
                    </td>
                    <td className={`px-3 py-2 font-semibold ${CORES_SITUACAO[sit]}`} data-testid={`situacao-${m.id}`}>
                      {rotuloSituacao(sit)}
                      {sit === 'paga' && m.paid_at ? (
                        <span className="text-micro text-[#71717A] font-normal">
                          {' '}({m.paid_at.split('-').reverse().join('/')})
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      {/* quem só VÊ o lançamento não mexe no dinheiro (mesma regra
                          da turma: o servidor manda, a tela só mostra) */}
                      {m.pode_editar === false ? (
                        <div className="flex justify-end">
                          <span className="text-micro text-[#71717A]"
                                data-testid={`sem-permissao-${m.id}`}
                                title="Você só pode ver os lançamentos desta turma">
                            só leitura
                          </span>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-1">
                          {sit === 'paga' ? (
                            <Button size="sm" variant="outline" onClick={() => desfazer(m)}
                                    data-testid={`desfazer-${m.id}`} title="Desfazer pagamento"
                                    className="border-[#27272A] text-[#A1A1AA] hover:text-white">
                              <Undo2 size={16} />
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => marcarPago(m)}
                                    data-testid={`pagar-${m.id}`} title="Registrar pagamento"
                                    className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10">
                              <CheckCircle2 size={16} />
                            </Button>
                          )}
                          {m.pode_apagar !== false && (
                            <Button size="sm" variant="outline" onClick={() => apagar(m)}
                                    data-testid={`apagar-${m.id}`} title="Apagar lançamento"
                                    className="border-[#27272A] text-red-400 hover:text-red-300">
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
